import { useState } from 'react'
import { CheckCircle, AlertTriangle, XCircle, AlertCircle, ChevronDown, ChevronUp, MapPin } from 'lucide-react'

export interface AuthenticityIndicator {
  score: number // 0-100
  level: 'VERIFIED' | 'PARTIAL' | 'COMPROMISED' | 'FAILED'
  color: '#00FF00' | '#10B981' | '#FFA500' | '#FF0000' | '#8B0000'
  icon: 'checkmark-circle' | 'warning' | 'close-circle' | 'alert-circle'
  message: string
  details: string[]
}

interface AuthenticityBadgeProps {
  authenticity?: AuthenticityIndicator | null
  size?: 'small' | 'medium' | 'large'
  showScore?: boolean
  onPress?: () => void
  className?: string
  expandable?: boolean
  onViewMap?: () => void
}

export default function AuthenticityBadge({
  authenticity,
  size = 'medium',
  showScore = false,
  onPress,
  className = '',
  expandable = true,
  onViewMap
}: AuthenticityBadgeProps) {
  const [detailsVisible, setDetailsVisible] = useState(false)

  // Early return if no authenticity data
  if (!authenticity) {
    return null
  }

  // Safe defaults
  const safeAuthenticity = {
    score: authenticity.score || 0,
    color: authenticity.color || '#10B981',
    icon: authenticity.icon || 'checkmark-circle',
    message: authenticity.message || 'Authenticity Check',
    details: authenticity.details || [],
    level: authenticity.level || 'PARTIAL',
    ...authenticity
  }

  // Size configurations
  const sizeConfig = {
    small: {
      dot: 'w-2 h-2',
      text: 'text-xs',
      container: 'px-2 py-1',
      icon: 'w-3 h-3'
    },
    medium: {
      dot: 'w-3 h-3',
      text: 'text-sm',
      container: 'px-3 py-1.5',
      icon: 'w-4 h-4'
    },
    large: {
      dot: 'w-4 h-4',
      text: 'text-base',
      container: 'px-4 py-2',
      icon: 'w-5 h-5'
    }
  }[size]

  // Get badge configuration based on score
  const getBadgeConfig = () => {
    const { score } = safeAuthenticity

    if (score >= 95) {
      return {
        bgColor: 'bg-green-50',
        borderColor: 'border-green-500',
        textColor: 'text-green-700',
        dotColor: 'bg-green-500',
        label: 'VERIFIED',
        icon: <CheckCircle className={sizeConfig.icon} />,
        pulse: true
      }
    }
    if (score >= 70) {
      return {
        bgColor: 'bg-green-50',
        borderColor: 'border-green-400',
        textColor: 'text-green-600',
        dotColor: 'bg-green-400',
        label: 'LIKELY AUTHENTIC',
        icon: <CheckCircle className={sizeConfig.icon} />,
        pulse: false
      }
    }
    if (score >= 40) {
      return {
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-500',
        textColor: 'text-orange-700',
        dotColor: 'bg-orange-500',
        label: 'UNCERTAIN',
        icon: <AlertTriangle className={sizeConfig.icon} />,
        pulse: false
      }
    }
    return {
      bgColor: 'bg-red-50',
      borderColor: 'border-red-500',
      textColor: 'text-red-700',
      dotColor: 'bg-red-500',
      label: 'SUSPICIOUS',
      icon: <XCircle className={sizeConfig.icon} />,
      pulse: false
    }
  }

  const config = getBadgeConfig()

  const handlePress = () => {
    if (onPress) {
      onPress()
    } else if (expandable) {
      setDetailsVisible(!detailsVisible)
    }
  }

  // Compact badge (small, no expansion)
  if (size === 'small' && !expandable) {
    return (
      <button
        onClick={handlePress}
        className={`inline-flex items-center gap-1.5 ${sizeConfig.container} ${config.bgColor} border ${config.borderColor} rounded-full ${className}`}
      >
        <div className={`${sizeConfig.dot} ${config.dotColor} rounded-full ${config.pulse ? 'animate-pulse' : ''}`} />
        <span className={`${sizeConfig.text} font-semibold ${config.textColor}`}>
          {config.label}
          {showScore && ` ${safeAuthenticity.score}%`}
        </span>
      </button>
    )
  }

  // Full badge with expansion
  return (
    <div className={`${className}`}>
      {/* Badge Header */}
      <button
        onClick={handlePress}
        className={`w-full flex items-center justify-between gap-3 ${sizeConfig.container} ${config.bgColor} border-2 ${config.borderColor} rounded-lg hover:opacity-80 transition-opacity`}
      >
        <div className="flex items-center gap-2">
          <div className={`${sizeConfig.dot} ${config.dotColor} rounded-full ${config.pulse ? 'animate-pulse' : ''}`} />
          <span className={`${sizeConfig.text} font-bold ${config.textColor}`}>
            {config.label}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {showScore && (
            <span className={`${sizeConfig.text} ${config.textColor} font-semibold`}>
              {safeAuthenticity.score}%
            </span>
          )}
          {expandable && (
            detailsVisible ? (
              <ChevronUp className={`${sizeConfig.icon} ${config.textColor}`} />
            ) : (
              <ChevronDown className={`${sizeConfig.icon} ${config.textColor}`} />
            )
          )}
        </div>
      </button>

      {/* Expanded Details */}
      {detailsVisible && (
        <div className={`mt-2 p-4 ${config.bgColor} border ${config.borderColor} rounded-lg space-y-3`}>
          {/* Score Display */}
          <div className="flex items-center justify-between">
            <span className={`text-xs font-semibold ${config.textColor} uppercase tracking-wide`}>
              Authenticity Score
            </span>
            <div className="flex items-center gap-2">
              <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full ${config.dotColor} transition-all duration-300`}
                  style={{ width: `${safeAuthenticity.score}%` }}
                />
              </div>
              <span className={`text-sm font-bold ${config.textColor}`}>
                {safeAuthenticity.score}/100
              </span>
            </div>
          </div>

          {/* Message */}
          <div>
            <p className={`text-sm ${config.textColor} font-medium`}>
              {safeAuthenticity.message}
            </p>
          </div>

          {/* Details List */}
          {safeAuthenticity.details.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Verification Details
              </p>
              {safeAuthenticity.details.map((detail, index) => (
                <div key={index} className="flex items-start gap-2">
                  <div className="mt-0.5">
                    {detail.startsWith('✅') ? (
                      <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                    ) : detail.startsWith('⚠️') ? (
                      <AlertTriangle className="w-3.5 h-3.5 text-orange-600" />
                    ) : detail.startsWith('❌') ? (
                      <XCircle className="w-3.5 h-3.5 text-red-600" />
                    ) : (
                      <AlertCircle className="w-3.5 h-3.5 text-gray-600" />
                    )}
                  </div>
                  <p className="text-xs text-gray-700 flex-1">
                    {detail.replace(/^[✅⚠️❌]\s*/, '')}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* View Map Button */}
          {onViewMap && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                onViewMap()
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
            >
              <MapPin className="w-4 h-4" />
              View Capture Location
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// Export score calculation helper
export function calculateAuthenticityScore(data: {
  timeSinceCapture?: number
  locationMatch?: boolean
  deviceMatch?: boolean
  hasMetadata?: boolean
  fileSize?: number
  screenDetection?: { isScreen: boolean; confidence: number }
  mlConfidence?: number
  communityValidated?: boolean
}): AuthenticityIndicator {
  let score = 100
  const details: string[] = []

  // Time-based checks
  if (data.timeSinceCapture && data.timeSinceCapture > 24 * 60 * 60 * 1000) {
    score -= 15
    details.push('⚠️ Uploaded more than 24 hours after capture')
  } else if (data.timeSinceCapture && data.timeSinceCapture < 60 * 1000) {
    details.push('✅ Uploaded within 1 minute of capture')
  }

  // Location checks
  if (data.locationMatch === false) {
    score -= 20
    details.push('❌ GPS location mismatch (>100km)')
  } else if (data.locationMatch === true) {
    details.push('✅ GPS location verified')
  }

  // Device checks
  if (data.deviceMatch === false) {
    score -= 25
    details.push('❌ Device signature mismatch')
  } else if (data.deviceMatch === true) {
    details.push('✅ Device signature matched')
  }

  // Metadata checks
  if (data.hasMetadata === false) {
    score -= 10
    details.push('⚠️ Minimal or missing EXIF metadata')
  } else if (data.hasMetadata === true) {
    details.push('✅ Complete EXIF metadata present')
  }

  // File size checks
  if (data.fileSize) {
    if (data.fileSize < 50000) {
      score -= 5
      details.push('⚠️ File size unusually small')
    } else if (data.fileSize > 50 * 1024 * 1024) {
      score -= 5
      details.push('⚠️ File size unusually large')
    }
  }

  // Screen detection
  if (data.screenDetection?.isScreen) {
    const penalty = Math.floor((data.screenDetection.confidence / 100) * 30)
    score -= penalty
    details.push(`❌ Screen content detected (${data.screenDetection.confidence}% confidence)`)
  } else {
    details.push('✅ No screen content patterns detected')
  }

  // ML analysis
  if (data.mlConfidence !== undefined) {
    if (data.mlConfidence < 50) {
      score -= 25
      details.push(`❌ Low ML authenticity confidence (${data.mlConfidence}%)`)
    } else if (data.mlConfidence < 80) {
      score -= 10
      details.push(`⚠️ Medium ML confidence (${data.mlConfidence}%)`)
    } else {
      score = Math.min(100, score + 5)
      details.push(`✅ High ML confidence (${data.mlConfidence}%)`)
    }
  }

  // Community validation
  if (data.communityValidated) {
    score = Math.min(100, score + 10)
    details.push('✅ Community validated')
  }

  // Ensure score is 0-100
  score = Math.max(0, Math.min(100, score))

  // Determine level and styling
  let level: 'VERIFIED' | 'PARTIAL' | 'COMPROMISED' | 'FAILED'
  let color: '#00FF00' | '#10B981' | '#FFA500' | '#FF0000' | '#8B0000'
  let icon: 'checkmark-circle' | 'warning' | 'close-circle' | 'alert-circle'
  let message: string

  if (score >= 95) {
    level = 'VERIFIED'
    color = '#00FF00'
    icon = 'checkmark-circle'
    message = 'This content has been verified as authentic with high confidence.'
  } else if (score >= 85) {
    level = 'PARTIAL'
    color = '#10B981'
    icon = 'checkmark-circle'
    message = 'This content appears authentic with minor inconsistencies.'
  } else if (score >= 50) {
    level = 'COMPROMISED'
    color = '#FFA500'
    icon = 'warning'
    message = 'This content has multiple authenticity concerns.'
  } else {
    level = 'FAILED'
    color = '#FF0000'
    icon = 'close-circle'
    message = 'This content has significant authenticity issues.'
  }

  return {
    score,
    level,
    color,
    icon,
    message,
    details
  }
}
